import { ref } from "vue";

/**
 * Replacement for `window.confirm`.
 *
 * The native dialog blocks the JS thread, ignores the app's chosen language
 * (its buttons follow the operating system), and in the Capacitor WebView it
 * looks like the browser has crashed rather than like part of the app. It was
 * in use at eight places, all of them destructive actions.
 *
 * Usage is the same shape as before, one `await` heavier:
 *
 *     if (!await confirm({ message: deleteWarning })) return;
 *
 * Only one dialog can be open at a time — a second call resolves the first as
 * cancelled, which is what a stacked confirmation should do anyway.
 */

export interface ConfirmRequest {
  message: string;
  /** Optional heading above the message. */
  title?: string;
  /** Label for the button that goes through with it. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Paints the confirm button red. Default for anything that deletes. */
  danger?: boolean;
}

const request = ref<ConfirmRequest | null>(null);
let resolveCurrent: ((value: boolean) => void) | null = null;

function settle(value: boolean): void {
  const resolve = resolveCurrent;
  resolveCurrent = null;
  request.value = null;
  resolve?.(value);
}

export function useConfirm() {
  function confirm(next: ConfirmRequest): Promise<boolean> {
    settle(false); // any dialog already open counts as declined
    request.value = next;
    return new Promise<boolean>((resolve) => {
      resolveCurrent = resolve;
    });
  }

  return {
    request,
    confirm,
    accept: () => settle(true),
    cancel: () => settle(false),
  };
}
